import { useSearchParams } from "react-router-dom";
import { Clock, Mail, MapPin, Phone } from "lucide-react";
import { IMG } from "@/lib/images";
import { Reveal } from "@/components/Reveal";
import PageHero from "@/components/PageHero";
import LeadForm from "@/components/LeadForm";
import { EMAIL, PHONE_DISPLAY, PHONE_TEL, usePageMeta } from "@/lib/site";

export default function Contact() {
  usePageMeta(
    "Request a Project Consultation",
    "Tell Black Sky Builders what you want to build. Whether you have plans or are still figuring out where to begin, our Salt Lake City team can help determine the next steps."
  );
  const [params] = useSearchParams();
  const interestFinancing = params.get("interest") === "financing";

  return (
    <div data-testid="contact-page">
      <PageHero
        eyebrow="Request a Project Consultation"
        lines={["Tell Us What", "You Want to Build"]}
        copy="Whether you already have plans or are still figuring out where to begin, tell us about your project and our team can help determine the next steps."
        image={IMG.blueprint}
      />

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-8 sm:py-24 lg:px-16">
        <div className="grid gap-10 lg:grid-cols-[1fr_360px]">
          <Reveal>
            <LeadForm initialFinancing={interestFinancing} />
          </Reveal>

          <div className="space-y-5">
            <Reveal delay={0.05}>
              <div className="border border-line bg-coal p-6" data-testid="contact-info-card">
                <h2 className="font-display text-lg font-bold uppercase tracking-tight">Reach Us Directly</h2>
                <ul className="mt-6 space-y-5 text-sm">
                  <li>
                    <a href={PHONE_TEL} data-testid="contact-phone-link" className="flex items-center gap-3 text-mist transition-colors hover:text-white">
                      <Phone className="h-4 w-4 shrink-0 text-steel" />
                      <span>
                        <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-fog">Call or text</span>
                        {PHONE_DISPLAY}
                      </span>
                    </a>
                  </li>
                  <li>
                    <a href={`mailto:${EMAIL}`} data-testid="contact-email-link" className="flex items-center gap-3 break-all text-mist transition-colors hover:text-white">
                      <Mail className="h-4 w-4 shrink-0 text-steel" />
                      <span>
                        <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-fog">Email</span>
                        {EMAIL}
                      </span>
                    </a>
                  </li>
                  <li className="flex items-center gap-3 text-mist">
                    <MapPin className="h-4 w-4 shrink-0 text-steel" />
                    <span>
                      <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-fog">Based in</span>
                      Salt Lake City, Utah
                    </span>
                  </li>
                  <li className="flex items-center gap-3 text-mist">
                    <Clock className="h-4 w-4 shrink-0 text-steel" />
                    <span>
                      <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-fog">Response</span>
                      We review every request and follow up promptly
                    </span>
                  </li>
                </ul>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="border border-line bg-ink p-6">
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-fog">Not sure where to begin?</p>
                <p className="mt-3 text-sm leading-relaxed text-mist">
                  That's normal. Most projects start as an idea and a property. Send what you know — we'll help
                  figure out cost, plans, permits, engineering, and financing from there.
                </p>
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="border border-line bg-coal p-6">
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-fog">Service area</p>
                <p className="mt-3 text-sm leading-relaxed text-mist">
                  Salt Lake City and surrounding Utah communities — with the ability to evaluate projects
                  throughout Utah.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
